/**
 * NotFoundPage — proper bilingual 404 landing instead of the silent
 * redirect-to-/ behavior the catch-all used to do. Crawler-friendly
 * (real page content + canonical "back home" CTAs) and respects the
 * current language prefix in the URL.
 *
 * Iter 74 — go-live readiness.
 */
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Home, LifeBuoy, ArrowLeft, Map } from 'lucide-react';

const COPY = {
  ja: {
    code: '404',
    eyebrow: 'お探しのページが見つかりません',
    title: 'このページは存在しないか、移動した可能性があります。',
    body: 'リンクが古い、URLにタイプミスがある、または該当ページが移動された可能性がございます。下記のいずれかからお探しの情報にお進みください。',
    cta_home: 'トップへ戻る',
    cta_help: 'ヘルプセンター',
    cta_sitemap: 'サイトマップ',
    cta_back: '前のページへ戻る',
    helpful: 'よく見られているページ',
    links: [
      { label: 'プランと料金', to: '/personal/pricing' },
      { label: '法人スイート', to: '/business-suite' },
      { label: '使い方ガイド', to: '/how-it-works' },
      { label: 'お客様の声', to: '/testimonials' },
      { label: 'よくあるご質問', to: '/faq' },
      { label: 'お問い合わせ', to: '/contact' },
    ],
    note: 'もしリンク切れにお気づきの場合は、サポートまでご連絡ください — お調べいたします。',
  },
  en: {
    code: '404',
    eyebrow: 'We could not find that page',
    title: 'The page you were looking for doesn’t exist or has moved.',
    body: 'The link may be outdated, the URL might have a typo, or the page may have been relocated. Here are a few ways to get back on track.',
    cta_home: 'Back to homepage',
    cta_help: 'Help center',
    cta_sitemap: 'Sitemap',
    cta_back: 'Go back one page',
    helpful: 'Popular pages',
    links: [
      { label: 'Plans & pricing', to: '/personal/pricing' },
      { label: 'Business Suite', to: '/business-suite' },
      { label: 'How it works', to: '/how-it-works' },
      { label: 'Customer stories', to: '/testimonials' },
      { label: 'FAQs', to: '/faq' },
      { label: 'Contact', to: '/contact' },
    ],
    note: 'If you found a broken link, please let support know — we’d love to fix it.',
  },
};

function prefixed(to, lang) {
  // Re-apply current locale prefix so internal links stay in-language.
  if (!to.startsWith('/')) return to;
  if (lang === 'ja') return `/ja${to}`;
  if (lang === 'en') return `/en${to}`;
  return to;
}

export default function NotFoundPage() {
  const { lang } = useLanguage();
  const loc = useLocation();
  const t = COPY[lang === 'ja' ? 'ja' : 'en'];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="not-found-page">
      <Header />
      <main id="main-content" className="flex-1">
        {/* Purple hero band matches Terms/Privacy/Policies */}
        <div className="bg-[#9200C7] text-white text-center py-10 sm:py-14">
          <div className="text-xs tracking-[0.3em] text-purple-200/90 font-bold">
            {t.code} · {t.eyebrow}
          </div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-black px-4 max-w-3xl mx-auto leading-tight">
            {t.title}
          </h1>
          <p className="mt-3 text-purple-100/90 text-sm max-w-xl mx-auto px-6">
            {t.body}
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 px-4">
            <Link
              to={prefixed('/', lang)}
              className="inline-flex items-center gap-2 bg-white text-[#9200C7] font-bold text-sm rounded-lg px-4 py-2 hover:bg-purple-50 transition-colors"
              data-testid="not-found-cta-home"
            >
              <Home className="w-4 h-4" strokeWidth={2.4} />
              {t.cta_home}
            </Link>
            <Link
              to={prefixed('/help', lang)}
              className="inline-flex items-center gap-2 bg-purple-900/40 text-white font-bold text-sm rounded-lg px-4 py-2 hover:bg-purple-900/60 transition-colors border border-purple-300/30"
              data-testid="not-found-cta-help"
            >
              <LifeBuoy className="w-4 h-4" strokeWidth={2.4} />
              {t.cta_help}
            </Link>
            <Link
              to={prefixed('/sitemap', lang)}
              className="inline-flex items-center gap-2 bg-purple-900/40 text-white font-bold text-sm rounded-lg px-4 py-2 hover:bg-purple-900/60 transition-colors border border-purple-300/30"
              data-testid="not-found-cta-sitemap"
            >
              <Map className="w-4 h-4" strokeWidth={2.4} />
              {t.cta_sitemap}
            </Link>
            <button
              type="button"
              onClick={() => window.history.length > 1 ? window.history.back() : null}
              className="inline-flex items-center gap-2 bg-transparent text-white font-bold text-sm rounded-lg px-3 py-2 hover:underline"
              data-testid="not-found-cta-back"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
              {t.cta_back}
            </button>
          </div>
          {/* Tiny path echo for debug parity with our other oops pages */}
          <p className="mt-4 text-[10px] font-mono text-purple-300/80 px-4 break-all">
            {loc.pathname}{loc.search}
          </p>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-10">
            <h2 className="text-lg font-bold text-[#9200C7] mb-4">
              {t.helpful}
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.links.map((l, i) => (
                <li key={i}>
                  <Link
                    to={prefixed(l.to, lang)}
                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/60 hover:bg-purple-50 hover:border-purple-200 px-4 py-3 text-sm font-medium text-gray-800 transition-colors"
                    data-testid={`not-found-popular-${i}`}
                  >
                    <span>{l.label}</span>
                    <span className="text-purple-500">→</span>
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-gray-500 leading-relaxed">
              {t.note}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
